import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Loader2, Search, Mail, MapPin, Phone, ShieldCheck, ShieldAlert, Clock } from 'lucide-react';
import { AdminAPI, resolveImage } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const STATUS_FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'pending', label: 'En attente' },
  { id: 'approved', label: 'Validés' },
  { id: 'rejected', label: 'Refusés' },
];

const SuppliersAdminTab = () => {
  const { toast } = useToast();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try { setSuppliers(await AdminAPI.allSuppliers()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const approve = async (sid) => {
    setBusy(sid);
    try {
      await AdminAPI.approveSupplier(sid);
      toast({ title: '✅ Fournisseur validé', description: 'Email de bienvenue envoyé.' });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setBusy(null); }
  };

  const reject = async (sid, name) => {
    if (!window.confirm(`Refuser ${name} ? Le fournisseur sera désactivé et notifié par email.`)) return;
    setBusy(sid);
    try {
      await AdminAPI.rejectSupplier(sid);
      toast({ title: 'Fournisseur refusé', description: 'Email de notification envoyé.' });
      load();
    } catch (e) {
      toast({ title: 'Erreur', description: e?.response?.data?.detail || 'Échec' });
    } finally { setBusy(null); }
  };

  const filtered = suppliers.filter(s => {
    const term = q.trim().toLowerCase();
    if (term && !(s.name || '').toLowerCase().includes(term) && !(s.owner_email || '').toLowerCase().includes(term) && !(s.city || '').toLowerCase().includes(term)) {
      return false;
    }
    if (filter === 'pending') return s.approved === false && !s.rejected;
    if (filter === 'approved') return s.approved === true;
    if (filter === 'rejected') return s.rejected === true;
    return true;
  });

  const status = (s) => {
    if (s.rejected) return { label: 'Refusé', cls: 'bg-rose-50 text-rose-700 border-rose-200', icon: XCircle };
    if (s.approved) return { label: 'Validé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: ShieldCheck };
    return { label: 'En attente', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock };
  };

  return (
    <div data-testid="admin-suppliers-tab">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A6F5C]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher par nom, email, ville…" className="pl-9" data-testid="admin-suppliers-search" />
        </div>
        <div className="flex gap-1 rounded-lg bg-[#FAF7F2] border border-[#E8E0D0] p-0.5">
          {STATUS_FILTERS.map(f => (
            <button key={f.id} onClick={() => setFilter(f.id)}
              data-testid={`admin-suppliers-filter-${f.id}`}
              className={`px-3 py-1.5 rounded text-xs font-medium ${filter === f.id ? 'bg-[#1F4D3F] text-white' : 'text-[#7A6F5C] hover:text-[#1F4D3F]'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#7A6F5C]"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Chargement…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#7A6F5C]"><ShieldAlert className="w-10 h-10 mx-auto mb-3 opacity-50" />Aucun fournisseur dans ce filtre.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3">Société</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Ville</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const st = status(s);
                const Icon = st.icon;
                return (
                  <tr key={s.id} className="border-t border-[#F5F1EA]" data-testid={`admin-supplier-row-${s.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {s.logo ? (
                          <img src={resolveImage(s.logo)} alt="" className="w-9 h-9 rounded object-cover" onError={e => { e.target.style.visibility = 'hidden'; }} />
                        ) : (
                          <div className="w-9 h-9 rounded bg-[#1F4D3F] text-white text-xs font-semibold flex items-center justify-center">
                            {(s.name || '?').slice(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="font-medium text-[#2D2A26]">{s.name}</div>
                          <div className="text-xs text-[#7A6F5C]">{s.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#5A5247]">
                      {s.owner_email ? (
                        <div className="text-xs">
                          <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.owner_email}</div>
                          {s.owner_name && <div className="text-[#7A6F5C] mt-0.5">{s.owner_name}</div>}
                        </div>
                      ) : <span className="text-xs text-[#7A6F5C]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#5A5247]"><span className="inline-flex items-center gap-1 text-xs"><MapPin className="w-3 h-3" /> {s.city || '—'}</span></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border ${st.cls}`}>
                        <Icon className="w-3 h-3" /> {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {s.approved !== true && (
                        <Button size="sm" onClick={() => approve(s.id)} disabled={busy === s.id}
                          className="bg-[#1F4D3F] hover:bg-[#173B30] text-white mr-1"
                          data-testid={`admin-supplier-approve-${s.id}`}>
                          <CheckCircle className="w-3.5 h-3.5 mr-1" /> Valider
                        </Button>
                      )}
                      {!s.rejected && (
                        <Button size="sm" variant="outline" onClick={() => reject(s.id, s.name)} disabled={busy === s.id}
                          className="border-rose-300 text-rose-600 hover:bg-rose-50"
                          data-testid={`admin-supplier-reject-${s.id}`}>
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Refuser
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default SuppliersAdminTab;
